// SentItemsPage — /sent-items. The page that replaces Agreements and Docs.
//
//   Miles: "The largest crime that currently exists is under the 'Agreements'
//   tab. This should be named 'Sent Items' and it should contain every type of
//   item sent out individually… Next, when you click on an item why the hell
//   would you display a barren panel of information?"
//
// So: one list of every item — sent, half-signed, still being prepared — three
// views over it (Q4), quick type filters, and a panel that actually says
// something. Every filter lives in the URL, including which item is open, so
// "the blocked sync on 77 S Roosevelt" is a link one operator sends another.
//
// The page does NOT sort or filter what it renders. The server does both, per
// view (§6.1); a client-side re-sort would interleave the moment "Load older"
// is pressed, and a client-side re-filter would disagree with the count above
// it. What arrives is what is shown.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { formatWhen } from '@/lib/commandCenterClient';
import { InspectorDrawer, OcEmpty, OcError, OcLoading } from '@/components/oc/bits';
import { describeWithheldKinds, describeWhere } from '@/lib/viewDocs';
import { Presets } from '@/components/sentItems/Presets';
import { SentItemList } from '@/components/sentItems/SentItemList';
import { SentItemPanel } from '@/components/sentItems/SentItemPanel';
import { StateFilters } from '@/components/sentItems/StateFilters';
import { TypeChips } from '@/components/sentItems/TypeChips';
import { ViewSwitcher } from '@/components/sentItems/ViewSwitcher';
import {
  applyParamsToSearch,
  hasActiveFilters,
  paramsFromSearch,
  readStoredView,
  viewIsPaginated,
  writeStoredView,
  type SentItemsFilterPatch,
} from '@/components/sentItems/sentItemsView';
import { fetchSentItem, fetchSentItems, sentItemsQuery } from '@/lib/sentItemsClient';
import type {
  SentItemDetailResponse,
  SentItemRow,
  SentItemsListParams,
  SentItemsListResponse,
  SentItemsView,
} from '@/lib/sentItemsClient';
import { fixtureDetail, fixtureListResponse } from '@/lib/sentItemsFixtures';

function listError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Couldn't load what has been sent (${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't load what has been sent.";
}

const EMPTY_FACETS: SentItemsListResponse['facets'] = {
  typeGroups: [],
  states: [],
  parties: [],
};

export function SentItemsPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // Dev-only: render the contract's fixture corpus with no API at all, so the
  // page is reviewable while W2 is still writing the routes it calls.
  const fixturesMode =
    Boolean(import.meta.env?.DEV) && searchParams.get('fixtures') === '1';

  // `params` is memoised on the WIRE QUERY rather than on the URL object, so
  // opening an item (which writes ?open=) does not look like a filter change
  // and re-fetch the whole list underneath the drawer.
  const listQuery = sentItemsQuery(paramsFromSearch(searchParams));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => paramsFromSearch(searchParams), [listQuery]);
  const view: SentItemsView = params.view ?? 'all';
  const openId = searchParams.get('open');
  const openVersion = searchParams.get('v');

  const [data, setData] = useState<SentItemsListResponse | null>(null);
  const [extra, setExtra] = useState<SentItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const liveRef = useRef(0);

  const [detail, setDetail] = useState<SentItemDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailRef = useRef(0);

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void, replace = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  // The remembered view (§6.1) applies only when the URL does not state one —
  // a shared link always opens on the view its sender was looking at.
  useEffect(() => {
    if (searchParams.get('view')) return;
    const stored = readStoredView();
    if (stored && stored !== 'all') {
      updateParams((p) => p.set('view', stored), true);
    }
    // Once, on mount: this must not fight a deliberate switch back to `all`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- the list ----
  useEffect(() => {
    if (fixturesMode) {
      setData(fixtureListResponse());
      setExtra([]);
      setLoading(false);
      setError(null);
      return;
    }
    const token = ++liveRef.current;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetchSentItems(params);
        if (liveRef.current !== token) return;
        setData(res);
        setExtra([]);
      } catch (err) {
        if (liveRef.current !== token) return;
        setError(listError(err));
      } finally {
        if (liveRef.current === token) setLoading(false);
      }
    })();
  }, [params, fixturesMode, reloadToken]);

  // ---- the open item ----
  useEffect(() => {
    if (!openId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    if (fixturesMode) {
      const hit = fixtureDetail(openId);
      setDetail(hit);
      setDetailError(hit ? null : 'No fixture detail for that row.');
      setDetailLoading(false);
      return;
    }
    const token = ++detailRef.current;
    setDetailLoading(true);
    setDetailError(null);
    void (async () => {
      try {
        const res = await fetchSentItem(openId, openVersion);
        if (detailRef.current !== token) return;
        setDetail(res);
      } catch (err) {
        if (detailRef.current !== token) return;
        setDetailError(listError(err));
      } finally {
        if (detailRef.current === token) setDetailLoading(false);
      }
    })();
  }, [openId, openVersion, fixturesMode, reloadToken]);

  const rows = useMemo(() => [...(data?.items ?? []), ...extra], [data, extra]);
  const nextCursor = data?.nextCursor ?? null;

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchSentItems({ ...params, cursor: nextCursor });
      const seen = new Set(rows.map((r) => r.itemId));
      setExtra((prev) => [...prev, ...res.items.filter((r) => !seen.has(r.itemId))]);
      setData((prev) => (prev ? { ...prev, nextCursor: res.nextCursor } : prev));
    } catch (err) {
      setError(listError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const patchParams = useCallback(
    (patch: SentItemsFilterPatch) => {
      updateParams((p) => {
        applyParamsToSearch(p, { ...paramsFromSearch(p), ...patch });
        // A filter change invalidates the page the open row came from.
        p.delete('v');
      });
    },
    [updateParams],
  );

  const setView = (next: SentItemsView) => {
    writeStoredView(next);
    patchParams({ view: next });
  };

  /**
   * A preset REPLACES the filters rather than patching them (§6.2 saved views).
   *
   * `applyParamsToSearch` writes every dimension it owns, clearing the ones the
   * next param set leaves unset — so "Blocked" after "Renewals" is Blocked, not
   * Blocked renewals. The remembered view follows the preset for the same
   * reason clicking the switcher does: it is the view this operator chose.
   */
  const selectPreset = useCallback(
    (next: SentItemsListParams) => {
      writeStoredView(next.view ?? 'all');
      updateParams((p) => {
        applyParamsToSearch(p, next);
        p.delete('v');
      });
    },
    [updateParams],
  );

  const openItem = useCallback(
    (itemId: string) =>
      updateParams((p) => {
        p.set('open', itemId);
        p.delete('v');
      }),
    [updateParams],
  );

  const closeItem = useCallback(
    () =>
      updateParams((p) => {
        p.delete('open');
        p.delete('v');
      }),
    [updateParams],
  );

  const withheld = data?.withheld ?? [];
  const withheldNote = describeWithheldKinds(withheld);
  const gaps = data?.gaps ?? [];
  const gapTotal = gaps.reduce((n, g) => n + g.count, 0);
  const total = data?.total ?? rows.length;
  const filtered = hasActiveFilters(params);

  const openRow = rows.find((r) => r.itemId === openId) ?? detail?.row ?? null;

  return (
    <div className="oc-page si-page">
      <header className="oc-page-header">
        <h1 className="oc-page-title">Sent Items</h1>
        <span className="oc-meta">
          Everything sent, and everything being prepared — one row each, from
          draft to filed.
          {data?.generatedAt ? ` As of ${formatWhen(data.generatedAt)}.` : ''}
        </span>
        {/* A secondary REPORT over the same corpus, not a fourth view (sol
            finding 17). The view switcher's three entries are three orderings
            of this list; that page's row is a SIGNATURE, so it gets a quiet
            link rather than a tab that would claim otherwise. */}
        <Link className="btn" data-variant="ghost" data-size="sm" to="/sent-items/signers">
          Signers
        </Link>
      </header>

      <div className="si-controls">
        <ViewSwitcher view={view} onChange={setView} />
        <span className="oc-meta si-count">
          {rows.length === total
            ? `${total} item${total === 1 ? '' : 's'}`
            : `${rows.length} of ${total}`}
        </span>
      </div>

      {/* The Agreements page's six saved views. Shortcuts onto the filters
          below — no server state, no `preset=` param (sol finding 17). */}
      <Presets params={params} onSelect={selectPreset} />

      <TypeChips
        selected={params.types ?? []}
        renewalsOnly={Boolean(params.renewalsOnly)}
        facets={data?.facets?.typeGroups ?? EMPTY_FACETS.typeGroups}
        // On the roster iff nothing is being withheld from this viewer (§5.7):
        // never a count, never a disabled chip, never a zero.
        showAccommodation={withheld.length === 0}
        onChange={({ types, renewalsOnly }) =>
          patchParams({
            types,
            ...(renewalsOnly ? { renewalsOnly: true } : { renewalsOnly: undefined }),
          })
        }
      />

      <StateFilters
        params={params}
        view={view}
        stateFacets={data?.facets?.states ?? EMPTY_FACETS.states}
        onChange={patchParams}
      />

      {filtered && (
        <div className="si-clear">
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            onClick={() =>
              updateParams((p) => {
                for (const key of [
                  'types',
                  'renewals',
                  'state',
                  'step',
                  'phase',
                  'party',
                  'blocked',
                  'q',
                  'from',
                  'to',
                ]) {
                  p.delete(key);
                }
              })
            }
          >
            Clear filters
          </button>
        </div>
      )}

      {/* A standing statement of scope, not a notification — the same posture
          the Docs list and the household docket take (C9). It is unconditional
          for an off-roster viewer, so it says nothing about whether anything
          is actually there. */}
      {withheldNote && (
        <div className="oc-meta si-withheld">{withheldNote}</div>
      )}

      {error && <OcError>{error}</OcError>}

      {gapTotal > 0 && (
        <div className="alert" data-tone="info">
          <div className="alert-body">
            {gapTotal} item{gapTotal === 1 ? '' : 's'} of{' '}
            {gaps.length === 1 ? 'one type' : `${gaps.length} types`} cannot be
            shown here yet, because {gaps.length === 1 ? 'it has' : 'they have'} no
            details panel written: {gaps.map((g) => `${g.kind} (${g.count})`).join(', ')}.
            Nothing is lost — they are excluded rather than shown with a guessed
            summary.
          </div>
        </div>
      )}

      {loading ? (
        <OcLoading label="Loading what has been sent…" />
      ) : (
        <SentItemList
          items={rows}
          view={view}
          onOpen={openItem}
          panelOpen={Boolean(openId)}
          emptyText={
            filtered
              ? 'Nothing matches those filters.'
              : view === 'all'
                ? 'Nothing has been sent or started yet.'
                : 'Nothing is in motion — everything sent is finished.'
          }
        />
      )}

      {viewIsPaginated(view) && nextCursor && !loading && (
        <div className="si-more-rows">
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}

      {openId && (
        <InspectorDrawer
          title={openRow?.subject || 'Loading…'}
          subtitle={
            openRow
              ? [openRow.kindLabel, describeWhere(openRow.propertyLabel, openRow.unitLabel)]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          onClose={closeItem}
        >
          {detailLoading && !detail ? (
            <OcLoading label="Loading the item…" />
          ) : detailError && !detail ? (
            <OcError>{detailError}</OcError>
          ) : detail ? (
            <SentItemPanel
              detail={detail}
              onChanged={() => setReloadToken((n) => n + 1)}
              onSelectVersion={(versionId) => updateParams((p) => p.set('v', versionId))}
            />
          ) : (
            <OcEmpty>Nothing to show for this item.</OcEmpty>
          )}
        </InspectorDrawer>
      )}
    </div>
  );
}
